import { useState, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Download, Printer, Image, RefreshCw, Upload, X } from "lucide-react";
import { toast } from "sonner";

// Animal SVG icons (simplified paths) - larger versions for background
const ANIMALS = {
  none: { name: "Aucun", icon: null },
  truck: { 
    name: "Food Truck", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(100, 95) scale(2.5)" opacity="${opacity}">
        <rect x="10" y="25" width="60" height="35" rx="5" fill="${color}" />
        <rect x="60" y="15" width="25" height="45" rx="3" fill="${color}" />
        <circle cx="25" cy="65" r="8" fill="${color}" />
        <circle cx="70" cy="65" r="8" fill="${color}" />
        <rect x="65" y="25" width="15" height="15" rx="2" fill="${color}" />
        <rect x="15" y="35" width="12" height="8" rx="1" fill="${color}" />
        <rect x="32" y="35" width="12" height="8" rx="1" fill="${color}" />
      </g>
    `
  },
  chef: { 
    name: "Toque de Chef", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(115, 100) scale(2.2)" opacity="${opacity}">
        <ellipse cx="40" cy="55" rx="30" ry="10" fill="${color}" />
        <path d="M15 55 Q15 20 40 15 Q65 20 65 55" fill="${color}" />
        <circle cx="25" cy="30" r="12" fill="${color}" />
        <circle cx="55" cy="30" r="12" fill="${color}" />
        <circle cx="40" cy="22" r="14" fill="${color}" />
      </g>
    `
  },
  burger: { 
    name: "Burger", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(105, 100) scale(2.4)" opacity="${opacity}">
        <path d="M10 35 Q10 15 40 15 Q70 15 70 35 Z" fill="${color}" />
        <rect x="8" y="35" width="64" height="8" rx="2" fill="${color}" />
        <rect x="8" y="43" width="64" height="6" rx="1" fill="${color}" />
        <rect x="8" y="49" width="64" height="8" rx="2" fill="${color}" />
        <rect x="8" y="57" width="64" height="10" rx="3" fill="${color}" />
      </g>
    `
  },
  pizza: { 
    name: "Pizza", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(110, 90) scale(2.5)" opacity="${opacity}">
        <path d="M40 10 L70 65 Q40 75 10 65 Z" fill="${color}" />
        <circle cx="30" cy="35" r="6" fill="${color}" />
        <circle cx="50" cy="40" r="6" fill="${color}" />
        <circle cx="35" cy="55" r="6" fill="${color}" />
      </g>
    `
  },
  taco: { 
    name: "Taco", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(100, 110) scale(2.5)" opacity="${opacity}">
        <path d="M10 50 Q40 10 70 50" fill="${color}" stroke="${color}" stroke-width="3" />
        <ellipse cx="40" cy="40" rx="25" ry="12" fill="${color}" />
      </g>
    `
  },
  crepe: { 
    name: "Crêpe", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(90, 120) scale(2.8)" opacity="${opacity}">
        <ellipse cx="40" cy="40" rx="35" ry="20" fill="${color}" />
        <ellipse cx="40" cy="35" rx="15" ry="8" fill="${color}" />
      </g>
    `
  },
  hotdog: { 
    name: "Hot-Dog", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(85, 130) scale(2.8)" opacity="${opacity}">
        <ellipse cx="40" cy="35" rx="35" ry="15" fill="${color}" />
        <ellipse cx="40" cy="30" rx="30" ry="10" fill="${color}" />
      </g>
    `
  },
  icecream: { 
    name: "Glace", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(120, 85) scale(2.5)" opacity="${opacity}">
        <path d="M25 40 L35 75 L45 40 Z" fill="${color}" />
        <circle cx="35" cy="30" r="20" fill="${color}" />
        <circle cx="25" cy="25" r="14" fill="${color}" />
        <circle cx="45" cy="28" r="12" fill="${color}" />
      </g>
    `
  },
  coffee: { 
    name: "Café", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(110, 95) scale(2.5)" opacity="${opacity}">
        <rect x="15" y="25" width="40" height="50" rx="5" fill="${color}" />
        <path d="M55 35 Q75 35 75 55 Q75 75 55 75" fill="none" stroke="${color}" stroke-width="6" />
        <ellipse cx="35" cy="25" rx="20" ry="6" fill="${color}" />
      </g>
    `
  },
  fries: { 
    name: "Frites", 
    icon: (color, opacity = 0.15) => `
      <g transform="translate(115, 85) scale(2.5)" opacity="${opacity}">
        <path d="M15 30 L10 75 L60 75 L55 30 Z" fill="${color}" />
        <rect x="18" y="12" width="7" height="45" rx="2" fill="${color}" />
        <rect x="27" y="8" width="7" height="50" rx="2" fill="${color}" />
        <rect x="36" y="10" width="7" height="48" rx="2" fill="${color}" />
        <rect x="45" y="6" width="7" height="52" rx="2" fill="${color}" />
      </g>
    `
  },
};

export default function BadgeGeneratorPage() {
  const [year, setYear] = useState("2026");
  const [foodTruckName, setFoodTruckName] = useState("");
  const [selectedAnimal, setSelectedAnimal] = useState("none");
  const [badgeColor, setBadgeColor] = useState("#8B1538");
  const [textColor, setTextColor] = useState("#FFFFFF");
  const [animalOpacity, setAnimalOpacity] = useState(0.15);
  const [generating, setGenerating] = useState(false);
  const [customImage, setCustomImage] = useState(null); // Data URL of uploaded image
  const [customImageName, setCustomImageName] = useState("");
  const badgeRef = useRef(null);
  const fileInputRef = useRef(null);

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomImage(e.target.result);
      setCustomImageName(file.name);
      setSelectedAnimal("custom"); // Switch to custom mode
      toast.success("Image téléchargée !");
    };
    reader.onerror = () => {
      toast.error("Erreur lors du chargement de l'image");
    };
    reader.readAsDataURL(file);
  };

  // Remove custom image
  const removeCustomImage = () => {
    setCustomImage(null);
    setCustomImageName("");
    setSelectedAnimal("none");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const downloadAsPNG = async () => {
    setGenerating(true);
    try {
      const svg = badgeRef.current;
      if (!svg) return;

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      
      const size = 1181;
      canvas.width = size;
      canvas.height = size;

      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      const img = new window.Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(url);

        const link = document.createElement("a");
        link.download = `badge-ftu-${year}${foodTruckName ? `-${foodTruckName.replace(/\s+/g, '-')}` : ""}.png`;
        link.href = canvas.toDataURL("image/png");
        link.click();
        
        toast.success("Badge téléchargé !");
        setGenerating(false);
      };
      img.onerror = () => {
        toast.error("Erreur lors de la génération");
        setGenerating(false);
      };
      img.src = url;
    } catch (error) {
      console.error("Error generating badge:", error);
      toast.error("Erreur lors de la génération");
      setGenerating(false);
    }
  };

  const printBadge = () => {
    const printWindow = window.open("", "_blank");
    const svg = badgeRef.current;
    if (!svg || !printWindow) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Badge Food Truck United ${year}</title>
          <style>
            @page { size: 10cm 10cm; margin: 0; }
            body { margin: 0; display: flex; justify-content: center; align-items: center; height: 100vh; }
            svg { width: 10cm; height: 10cm; }
          </style>
        </head>
        <body>
          ${svgData}
          <script>window.onload = () => { window.print(); window.close(); }</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const getAnimalSVG = () => {
    const animal = ANIMALS[selectedAnimal];
    if (!animal || !animal.icon) return "";
    return animal.icon(badgeColor, animalOpacity);
  };

  // Mini icon for preview grid (smaller, full opacity)
  const getMiniIcon = (key, color) => {
    const icons = {
      truck: `<g transform="scale(0.5) translate(20, 15)"><rect x="10" y="25" width="60" height="35" rx="5" fill="${color}" /><rect x="60" y="15" width="25" height="45" rx="3" fill="${color}" /><circle cx="25" cy="65" r="8" fill="${color}" /><circle cx="70" cy="65" r="8" fill="${color}" /></g>`,
      chef: `<g transform="scale(0.5) translate(20, 10)"><ellipse cx="40" cy="55" rx="30" ry="10" fill="${color}" /><path d="M15 55 Q15 20 40 15 Q65 20 65 55" fill="${color}" /><circle cx="25" cy="30" r="12" fill="${color}" /><circle cx="55" cy="30" r="12" fill="${color}" /><circle cx="40" cy="22" r="14" fill="${color}" /></g>`,
      burger: `<g transform="scale(0.5) translate(20, 15)"><path d="M10 35 Q10 15 40 15 Q70 15 70 35 Z" fill="${color}" /><rect x="8" y="35" width="64" height="8" rx="2" fill="#4CAF50" /><rect x="8" y="43" width="64" height="6" rx="1" fill="#8B4513" /><rect x="8" y="49" width="64" height="8" rx="2" fill="#FFC107" /><rect x="8" y="57" width="64" height="10" rx="3" fill="${color}" /></g>`,
      pizza: `<g transform="scale(0.5) translate(20, 10)"><path d="M40 10 L70 65 Q40 75 10 65 Z" fill="#FFC107" /><circle cx="30" cy="35" r="6" fill="#E53935" /><circle cx="50" cy="40" r="6" fill="#E53935" /><circle cx="35" cy="55" r="6" fill="#E53935" /></g>`,
      taco: `<g transform="scale(0.5) translate(20, 20)"><path d="M10 50 Q40 10 70 50" fill="#FFC107" stroke="${color}" stroke-width="3" /><ellipse cx="40" cy="40" rx="25" ry="12" fill="#4CAF50" /></g>`,
      crepe: `<g transform="scale(0.5) translate(20, 20)"><ellipse cx="40" cy="40" rx="35" ry="20" fill="#DEB887" /><ellipse cx="40" cy="35" rx="15" ry="8" fill="#FFF59D" /><circle cx="30" cy="45" r="4" fill="#E53935" /><circle cx="50" cy="45" r="4" fill="#E53935" /></g>`,
      hotdog: `<g transform="scale(0.5) translate(20, 25)"><ellipse cx="40" cy="35" rx="35" ry="15" fill="#DEB887" /><ellipse cx="40" cy="30" rx="30" ry="10" fill="${color}" /><path d="M15 28 Q40 20 65 28" fill="none" stroke="#FFC107" stroke-width="3" /></g>`,
      icecream: `<g transform="scale(0.5) translate(25, 5)"><path d="M25 40 L35 70 L45 40 Z" fill="#DEB887" /><circle cx="35" cy="30" r="18" fill="${color}" /><circle cx="25" cy="25" r="12" fill="#FFCDD2" /><circle cx="45" cy="28" r="10" fill="#C8E6C9" /></g>`,
      coffee: `<g transform="scale(0.5) translate(25, 15)"><rect x="15" y="25" width="40" height="45" rx="5" fill="${color}" /><path d="M55 35 Q70 35 70 50 Q70 65 55 65" fill="none" stroke="${color}" stroke-width="5" /><ellipse cx="35" cy="25" rx="20" ry="5" fill="${color}" /></g>`,
      fries: `<g transform="scale(0.5) translate(25, 10)"><path d="M15 30 L10 70 L60 70 L55 30 Z" fill="${color}" /><rect x="18" y="15" width="6" height="40" rx="2" fill="#FFC107" /><rect x="26" y="10" width="6" height="45" rx="2" fill="#FFC107" /><rect x="34" y="12" width="6" height="43" rx="2" fill="#FFC107" /><rect x="42" y="8" width="6" height="47" rx="2" fill="#FFC107" /></g>`,
    };
    return icons[key] || "";
  };

  return (
    <div className="min-h-screen bg-[#F9F7F5] p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary">Générateur de Badge</h1>
          <p className="text-muted-foreground mt-2">Créez des autocollants circulaires 10x10cm pour les membres</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Preview */}
          <Card className="order-1 md:order-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Aperçu du Badge
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center">
              <div className="bg-white p-4 rounded-lg shadow-inner border-2 border-dashed border-gray-200">
                <svg
                  ref={badgeRef}
                  viewBox="0 0 400 400"
                  width="300"
                  height="300"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Background circle */}
                  <circle cx="200" cy="200" r="195" fill={badgeColor} />
                  
                  {/* Inner white circle */}
                  <circle cx="200" cy="200" r="175" fill="white" />
                  
                  {/* Clip path for custom image */}
                  <defs>
                    <clipPath id="innerCircleClip">
                      <circle cx="200" cy="200" r="145" />
                    </clipPath>
                  </defs>
                  
                  {/* Custom uploaded image as background */}
                  {customImage && selectedAnimal === "custom" && (
                    <g clipPath="url(#innerCircleClip)">
                      <image
                        href={customImage}
                        x="55"
                        y="55"
                        width="290"
                        height="290"
                        preserveAspectRatio="xMidYMid slice"
                        opacity={animalOpacity + 0.3}
                      />
                    </g>
                  )}
                  
                  {/* Animal SVG as faded background */}
                  {selectedAnimal !== "none" && selectedAnimal !== "custom" && (
                    <g dangerouslySetInnerHTML={{ __html: getAnimalSVG() }} />
                  )}
                  
                  {/* Colored ring for text */}
                  <circle cx="200" cy="200" r="175" fill="none" stroke={badgeColor} strokeWidth="50" />
                  
                  {/* Text path definition - radius adjusted to be centered in the colored ring */}
                  <defs>
                    <path
                      id="textPathTop"
                      d="M 200,200 m -165,0 a 165,165 0 1,1 330,0"
                      fill="none"
                    />
                    <path
                      id="textPathBottom"
                      d="M 200,200 m -165,0 a 165,165 0 0,0 330,0"
                      fill="none"
                    />
                  </defs>
                  
                  {/* Top text - "MEMBRE DE" */}
                  <text fill={textColor} fontSize="20" fontWeight="bold" fontFamily="Arial, sans-serif">
                    <textPath href="#textPathTop" startOffset="50%" textAnchor="middle">
                      ★ MEMBRE DE ★
                    </textPath>
                  </text>
                  
                  {/* Bottom text - "FOOD TRUCK UNITED 2026" - flipped to read correctly */}
                  <text fill={textColor} fontSize="18" fontWeight="bold" fontFamily="Arial, sans-serif">
                    <textPath href="#textPathBottom" startOffset="50%" textAnchor="middle">
                      ★ FOOD TRUCK UNITED {year} ★
                    </textPath>
                  </text>
                  
                  {/* Center FTU Logo - on top of background */}
                  <g>
                    <circle cx="200" cy="200" r="55" fill="white" fillOpacity="0.95" stroke={badgeColor} strokeWidth="2" />
                    <text x="200" y="190" textAnchor="middle" fill={badgeColor} fontSize="11" fontWeight="bold" fontFamily="Arial, sans-serif">FOOD TRUCK</text>
                    <text x="200" y="208" textAnchor="middle" fill={badgeColor} fontSize="13" fontWeight="bold" fontFamily="Arial, sans-serif">UNITED</text>
                    <line x1="165" y1="218" x2="235" y2="218" stroke={badgeColor} strokeWidth="1" />
                    <text x="200" y="230" textAnchor="middle" fill={badgeColor} fontSize="8" fontFamily="Arial, sans-serif">COLLECTIF</text>
                  </g>
                  
                  {/* Food Truck name if provided - at bottom */}
                  {foodTruckName && (
                    <>
                      <rect x="80" y="315" width="240" height="32" rx="16" fill={badgeColor} />
                      <text x="200" y="337" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold" fontFamily="Arial, sans-serif">
                        {foodTruckName.toUpperCase().substring(0, 25)}
                      </text>
                    </>
                  )}
                </svg>
              </div>
              
              <p className="text-sm text-muted-foreground mt-4 text-center">
                Taille réelle : 10 × 10 cm
              </p>

              {/* Action buttons */}
              <div className="flex gap-3 mt-6">
                <Button onClick={downloadAsPNG} disabled={generating} className="bg-primary hover:bg-primary/90">
                  {generating ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  Télécharger PNG
                </Button>
                <Button onClick={printBadge} variant="outline">
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimer
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Settings */}
          <Card className="order-2 md:order-1">
            <CardHeader>
              <CardTitle>Personnalisation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Année</Label>
                <Input
                  type="text"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  placeholder="2026"
                  maxLength={4}
                />
              </div>

              <div>
                <Label>Nom du Food Truck</Label>
                <Input
                  type="text"
                  value={foodTruckName}
                  onChange={(e) => setFoodTruckName(e.target.value)}
                  placeholder="Ex: La Crêpe de Kopoik"
                  maxLength={25}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Nom affiché en bas du badge
                </p>
              </div>

              {/* Custom Image Upload */}
              <div className="p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                <Label className="mb-2 block font-semibold">Télécharger votre image</Label>
                
                {customImage ? (
                  <div className="flex items-center gap-3">
                    <img 
                      src={customImage} 
                      alt="Preview" 
                      className="w-16 h-16 object-cover rounded-lg border"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium truncate">{customImageName}</p>
                      <p className="text-xs text-muted-foreground">Image personnalisée</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={removeCustomImage}
                      className="text-red-600 hover:text-red-700"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div 
                    className="flex flex-col items-center justify-center py-4 cursor-pointer hover:bg-gray-100 rounded-lg transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600">Cliquez pour télécharger</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG jusqu'à 5 Mo</p>
                  </div>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="image-upload-input"
                />
              </div>

              <div>
                <Label>Ou choisir un fond prédéfini</Label>
                <Select 
                  value={selectedAnimal} 
                  onValueChange={(val) => {
                    setSelectedAnimal(val);
                    if (val !== "custom") {
                      // Don't clear custom image, just switch view
                    }
                  }}
                >
                  <SelectTrigger data-testid="animal-select">
                    <SelectValue placeholder="Choisir un fond" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun fond</SelectItem>
                    {customImage && <SelectItem value="custom">📷 Ma photo</SelectItem>}
                    {Object.entries(ANIMALS).filter(([key]) => key !== "none").map(([key, animal]) => (
                      <SelectItem key={key} value={key}>
                        {animal.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Opacity slider */}
              {(selectedAnimal !== "none") && (
                <div>
                  <Label>Intensité du fond ({Math.round((selectedAnimal === "custom" ? animalOpacity + 0.3 : animalOpacity) * 100)}%)</Label>
                  <input
                    type="range"
                    min="0.05"
                    max="0.5"
                    step="0.05"
                    value={animalOpacity}
                    onChange={(e) => setAnimalOpacity(parseFloat(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>Léger</span>
                    <span>Intense</span>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Couleur du badge</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={badgeColor}
                      onChange={(e) => setBadgeColor(e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={badgeColor}
                      onChange={(e) => setBadgeColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Couleur du texte</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Preset colors */}
              <div>
                <Label className="mb-2 block">Couleurs prédéfinies</Label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { bg: "#8B1538", text: "#FFFFFF", name: "Bordeaux" },
                    { bg: "#1E3A5F", text: "#FFFFFF", name: "Bleu nuit" },
                    { bg: "#2D5016", text: "#FFFFFF", name: "Vert forêt" },
                    { bg: "#D4AF37", text: "#1A1A1A", name: "Or" },
                    { bg: "#1A1A1A", text: "#FFFFFF", name: "Noir" },
                    { bg: "#E53935", text: "#FFFFFF", name: "Rouge" },
                    { bg: "#FF6F00", text: "#FFFFFF", name: "Orange" },
                  ].map((preset) => (
                    <button
                      key={preset.name}
                      onClick={() => {
                        setBadgeColor(preset.bg);
                        setTextColor(preset.text);
                      }}
                      className="px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-all hover:scale-105"
                      style={{ backgroundColor: preset.bg, color: preset.text, borderColor: preset.bg }}
                    >
                      {preset.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Animal preview grid */}
              <div>
                <Label className="mb-2 block">Icônes prédéfinies</Label>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(ANIMALS).filter(([key]) => key !== "none").map(([key, animal]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedAnimal(key)}
                      className={`p-2 rounded-lg border-2 transition-all hover:scale-105 ${selectedAnimal === key ? 'border-primary bg-primary/10' : 'border-gray-200 bg-white'}`}
                      title={animal.name}
                    >
                      <svg viewBox="0 0 50 50" width="40" height="40">
                        <g dangerouslySetInnerHTML={{ __html: getMiniIcon(key, selectedAnimal === key ? badgeColor : '#666') }} />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>

              {/* Tips */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">Conseils d'impression</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Utilisez du papier autocollant vinyle pour une meilleure durabilité</li>
                  <li>• Imprimez à 100% (pas de mise à l'échelle)</li>
                  <li>• Pour une découpe parfaite, utilisez un cutter circulaire</li>
                  <li>• Le PNG est en haute résolution (300 DPI)</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
